import { useCallback, useEffect, useRef, useState, KeyboardEvent, forwardRef } from 'react';
import { validators } from '@sendible/common';
import { Chip, ChipValue, Container, Footer, Wrapper } from './index.styles';
import { Button } from '../Button';

export interface ChipTextInputProps extends Component {
  /**
   * Component ID. Required.
   */
  id: string;
  /**
   * Generic validation error message shown when a value does not pass `validate`.
   * Defaults to 'Invalid value'. Optional.
   */
  errorText?: string;
  /**
   * Error message displayed when user attempts to exceed `max` chips.
   * Defaults to 'Maximum entries reached'. Optional.
   */
  errorMaxText?: string;
  /**
   * Placeholder text shown while there are fewer chips than `max`.
   * Hidden automatically once the limit is met. Optional.
   */
  placeholder?: string;
  /**
   * Maximum number of chips allowed. Defaults to 20. Optional.
   */
  max?: number;
  /**
   * Initial uncontrolled chip values. Ignored when `chips` (controlled) is provided. Optional.
   */
  initialChips?: string[];
  /**
   * Controlled chip values. When supplied the component becomes controlled and will not
   * manage its own internal chip state. Optional.
   */
  chips?: string[];
  /**
   * Forces the component into a max-reached visual/error state externally (e.g. parent side effects).
   * Optional.
   */
  reachMax?: boolean;
  /**
   * Callback fired with the updated list of chips after add or delete operations. Required.
   */
  onChange: (chips: string[]) => void;
  /**
   * Callback indicating whether the input currently presents an error (validation or max exceeded). Optional.
   */
  onErrorChange?: (hasError: boolean) => void;
  /**
   * Custom validation function. Return true for a valid chip. Defaults to `validators.validateEmail`. Optional.
   */
  validate?: (value: string) => boolean;
}

interface KeyMap {
  backspace: number;
  tab: number;
  enter: number;
  space: number;
}

export const ChipTextInput = forwardRef<HTMLDivElement, ChipTextInputProps>(
  (
    {
      className,
      customClass,
      errorText = 'Invalid value',
      errorMaxText = 'Maximum entries reached',
      id,
      initialChips = [],
      chips: controlledChips,
      max = 20,
      onChange,
      onErrorChange,
      placeholder = '',
      reachMax,
      testId = 'chip-text-input',
      validate = validators.validateEmail,
    },
    ref
  ) => {
    const KEY: KeyMap = { backspace: 8, tab: 9, enter: 13, space: 32 };
    const isControlled = Array.isArray(controlledChips);
    const [uncontrolledChips, setUncontrolledChips] = useState<string[]>(initialChips);
    const chips = isControlled ? (controlledChips as string[]) : uncontrolledChips;
    const [error, setError] = useState(false);
    const [maxReached, setMaxReached] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (reachMax) {
        setError(true);
        setMaxReached(true);
      }
    }, [reachMax]);

    useEffect(() => {
      if (isControlled) {
        setUncontrolledChips(controlledChips as string[]);
      }
    }, [isControlled, controlledChips]);

    const resetErrors = useCallback(() => {
      setError(false);
      setMaxReached(false);
      onErrorChange?.(false);
    }, [onErrorChange]);

    const handleDelete = useCallback(
      (chip: string) => {
        const update = (prev: string[]) => {
          if (!prev.includes(chip)) return prev;

          const updated = prev.filter((c) => c !== chip);
          const overMax = updated.length > max;

          setError(overMax);
          setMaxReached(overMax);
          onErrorChange?.(overMax);

          return updated;
        };

        if (isControlled) {
          const updated = update(chips);

          onChange(updated);
        } else {
          setUncontrolledChips(update);
          onChange(update(chips));
        }
      },
      [max, onErrorChange, chips, isControlled, onChange]
    );

    const commitChip = useCallback(
      (raw: string) => {
        const value = raw.trim().toLowerCase();

        if (!value) return;
        if (chips.includes(value)) return; // prevent duplicates

        if (chips.length >= max) {
          setError(true);
          setMaxReached(true);
          onErrorChange?.(true);

          return;
        }

        if (validate(value)) {
          const updated = [...chips, value];

          if (isControlled) {
            onChange(updated);
          } else {
            setUncontrolledChips(updated);
            onChange(updated);
          }
          resetErrors();
        } else {
          setError(true);
          setMaxReached(false);
          onErrorChange?.(true);
        }
      },
      [chips, max, onErrorChange, resetErrors, validate, isControlled, onChange]
    );

    const handleInputChange = () => {
      if (error) {
        resetErrors();
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const keyCode = e.keyCode || e.which;
      const { currentTarget } = e;

      if ([KEY.enter, KEY.space, KEY.tab].includes(keyCode) && currentTarget.value) {
        e.preventDefault();
        commitChip(currentTarget.value);
        currentTarget.value = '';

        return;
      }

      if (keyCode === KEY.backspace && !currentTarget.value && chips.length) {
        handleDelete(chips[chips.length - 1]);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      resetErrors();
      if (e.target.value) {
        commitChip(e.target.value);
        e.target.value = '';
      }
    };

    const dynamicPlaceholder = !max || chips.length < max ? placeholder : '';

    return (
      <Container
        className={`${customClass} ${className}`}
        data-testid={testId}
        id={id}
        ref={ref}
      >
        <Wrapper
          $hasError={error}
          $hasSelection={chips.length > 0}
          onClick={() => inputRef.current?.focus()}
          role="group"
        >
          {chips.map((chip) => (
            <Chip
              key={chip}
              title={chip}
            >
              <ChipValue>{chip}</ChipValue>
              <Button
                appearance="default"
                icon="close"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(chip);
                }}
                size={12}
                variation="default"
              />
            </Chip>
          ))}
          <input
            ref={inputRef}
            id={`${id}-input`}
            placeholder={dynamicPlaceholder}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            onBlur={handleBlur}
            type="text"
          />
        </Wrapper>
        <Footer>{error && <div className="error">{maxReached ? errorMaxText : errorText}</div>}</Footer>
      </Container>
    );
  }
);

ChipTextInput.displayName = 'ChipTextInput';

export default ChipTextInput;
